<template>
    <div class="attachment-upload">
        <el-upload class="upload-demo" action="#" accept=".doc,.docx,.xls,.xlsx,.ppt,.pptx,.pdf,.jpg,.jpeg,.png,.gif,.webp" :multiple="true" :show-file-list="false" :before-upload="beforeUpload"
            :http-request="uploadFile" :disabled="uploading">
            <el-button size="small" type="primary" :loading="uploading">
                {{ uploading ? '上传中...' : '点击上传附件' }}
            </el-button>
        </el-upload>
        <div class="upload-hint">支持 Word、Excel、PPT、PDF 和图片，单个文件不超过 50 MB</div>

        <!-- 附件列表 -->
        <div class="attachment-list" v-if="fileList.length > 0">
            <div v-for="(file, index) in fileList" :key="index" class="attachment-item">
                <div class="file-info" @click="file.status === 'success' ? downloadFile(file) : null">
                    <i class="el-icon-document file-icon"></i>
                    <span class="file-name" :class="{ 'can-download': file.status === 'success' }">{{ file.name }}</span>
                    <span class="file-size">({{ formatFileSize(file.size) }})</span>
                </div>
                <div class="file-actions">
                    <i class="el-icon-close delete-icon" @click="removeFile(index)" title="删除"></i>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import * as qiniu from 'qiniu-js';

export default {
    name: 'AttachmentUpload',
    props: {
        value: {
            type: Array,
            default: () => []
        }
    },
    data() {
        return {
            fileList: [],
            uploading: false,
            qiniuToken: ''
        };
    },
    watch: {
        value: {
            immediate: true,
            handler(newVal) {
                this.fileList = Array.isArray(newVal) ? newVal : [];
            }
        }
    },
    methods: {
        // 上传前的验证
        beforeUpload(file) {
            const extension = (file.name.split('.').pop() || '').toLowerCase();
            if (!['doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx', 'pdf', 'jpg', 'jpeg', 'png', 'gif', 'webp'].includes(extension)) {
                this.$message.error('只支持 Word、Excel、PPT、PDF 文档或图片');
                return false;
            }
            if (!file.size || file.size > 50 * 1024 * 1024) {
                this.$message.error('上传文件大小不能超过 50 MB');
                return false;
            }
            return true;
        },

        // 获取七牛云token
        async getQiniuToken() {
            try {
                const res = await this.$api.qiniuToken();
                if (res.code === 0) {
                    this.qiniuToken = res.data;
                    return true;
                } else {
                    this.$message.error('获取上传凭证失败');
                    return false;
                }
            } catch (error) {
                this.$message.error('获取上传凭证失败');
                return false;
            }
        },

        // 自定义上传方法
        async uploadFile(request) {
            const file = request.file;

            // 添加到文件列表
            const fileItem = {
                uid: Date.now() + Math.random(),
                name: file.name,
                size: file.size,
                type: file.type,
                status: 'uploading',
                percent: 0,
                raw: file
            };
            this.fileList.push(fileItem);

            // 获取七牛云token
            const hasToken = await this.getQiniuToken();
            if (!hasToken) {
                fileItem.status = 'error';
                this.updateFileList();
                return;
            }

            this.uploading = true;

            // 生成唯一key
            const key = `announcement/${Date.now()}_${Math.random().toString(36).substr(2)}_${file.name}`;

            // 七牛云配置
            const config = {
                useCdnDomain: true,
                region: qiniu.region.z0 // 根据你的存储区域修改
            };

            const putExtra = {
                fname: file.name,
                params: {},
                mimeType: null
            };

            // 创建上传观察对象
            const observable = qiniu.upload(
                file,
                key,
                this.qiniuToken,
                putExtra,
                config
            );

            // 订阅上传进度
            observable.subscribe({
                next: (response) => {
                    fileItem.percent = parseInt(response.total.percent);
                },
                error: (err) => {
                    console.error('上传失败', err);
                    fileItem.status = 'error';
                    this.uploading = false;
                    this.updateFileList();
                    this.$message.error('文件上传失败');
                },
                complete: (response) => {
                    // 上传成功，获取文件URL
                    const url = 'https://fenxiao.muruji.com/' + response.key;
                    fileItem.status = 'success';
                    fileItem.url = url;
                    fileItem.key = response.key;
                    this.uploading = false;
                    this.updateFileList();
                    this.$message.success('文件上传成功');
                }
            });

            // 如果需要取消上传，可以调用 subscription.unsubscribe()
        },

        // 更新文件列表并通知父组件
        updateFileList() {
            // 过滤掉原始文件对象，只保留需要的信息
            const formattedList = this.fileList.map(item => {
                return {
                    uid: item.uid,
                    name: item.name,
                    size: item.size,
                    type: item.type,
                    status: item.status,
                    url: item.url,
                    key: item.key
                };
            });

            this.$emit('input', formattedList);
        },

        // 删除文件
        removeFile(index) {
            this.fileList.splice(index, 1);
            this.updateFileList();
        },

        // 下载文件
        downloadFile(file) {
            if (file.url) {
                window.open(file.url, '_blank');
            } else {
                this.$message.warning('文件链接无效');
            }
        },

        // 格式化文件大小
        formatFileSize(size) {
            if (size < 1024) {
                return size + ' B';
            } else if (size < 1024 * 1024) {
                return (size / 1024).toFixed(2) + ' KB';
            } else {
                return (size / (1024 * 1024)).toFixed(2) + ' MB';
            }
        }
    }
};
</script>

<style lang="scss" scoped>
.attachment-upload {
    width: 100%;
}
.upload-hint{margin-top:8px;color:#909399;font-size:12px}

.attachment-list {
    margin-top: 16px;
}

.attachment-item {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 8px 12px;
    margin-bottom: 8px;
    border: 1px solid #ebeef5;
    border-radius: 4px;
    background-color: #f5f7fa;
    transition: all 0.3s;
    
    &:hover {
        // background-color: #e6f7ff;
        // border-color: #91d5ff;
    }
}

.file-info {
    display: flex;
    align-items: center;
    flex: 1;
    cursor: default;
    
    .can-download {
        cursor: pointer;
        color: #1E1E1E;
        
        &:hover {
            text-decoration: underline;
        }
    }
}

.file-icon {
    margin-right: 8px;
    color: #1E1E1E;
}

.file-name {
    margin-right: 8px;
    max-width: 300px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.file-size {
    color: #909399;
    margin-right: 12px;
}

.upload-progress {
    color: #E6A23C;
}

.upload-success {
    color: #67C23A;
}

.upload-error {
    color: #F56C6C;
}

.file-actions {
    .delete-icon {
        color: #F56C6C;
        font-size: 16px;
        cursor: pointer;
        
        &:hover {
            color: #f78989;
        }
    }
}
</style>
